import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, Trash2, FolderOpen } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AdminPageHeader,
  AdminSheet,
  AdminEmptyState,
  AdminTableSkeleton,
  DeleteConfirmDialog,
} from './shared';
import { ICON_OPTIONS, getIconComponent } from '@/lib/admin/icons';
import { useAdminCrud } from '@/hooks/useAdminCrud';
import { generateSlug } from '@/lib/utils';

interface DocCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  display_order: number | null;
  is_active: boolean | null;
  created_at: string;
  updated_at: string;
}

interface DocCategoryFormData {
  name: string;
  slug: string;
  description: string;
  icon: string;
  display_order: number;
  is_active: boolean;
}

export function DocCategoriesManager() {
  const {
    items: categories,
    loading,
    editorOpen,
    setEditorOpen,
    editingItem: editingCategory,
    formData,
    setFormData,
    isSaving,
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete: categoryToDelete,
    isDeleting,
    openCreateEditor,
    openEditEditor,
    handleSave,
    confirmDelete,
    handleDelete,
  } = useAdminCrud<DocCategory, DocCategoryFormData>({
    table: 'doc_categories',
    select: '*',
    orderBy: { column: 'display_order' },
    entityName: 'category',
    getInitialFormData: () => ({
      name: '',
      slug: '',
      description: '',
      icon: '',
      display_order: 0,
      is_active: true,
    }),
    mapItemToForm: (cat) => ({
      name: cat.name,
      slug: cat.slug,
      description: cat.description || '',
      icon: cat.icon || '',
      display_order: cat.display_order || 0,
      is_active: cat.is_active ?? true,
    }),
    buildPayload: (form) => ({
      name: form.name,
      slug: form.slug || generateSlug(form.name),
      description: form.description || null,
      icon: form.icon || null,
      display_order: form.display_order,
      is_active: form.is_active,
    }),
    validatePayload: (form) => (!form.name ? 'Name is required' : null),
  });

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Doc Categories"
        count={categories.length}
        onAdd={openCreateEditor}
        addLabel="Add Category"
      />

      {loading ? (
        <AdminTableSkeleton columns={5} rows={4} />
      ) : categories.length === 0 ? (
        <AdminEmptyState
          icon={FolderOpen}
          title="No doc categories yet"
          description="Create categories to organize your documentation articles."
          actionLabel="Add Category"
          onAction={openCreateEditor}
        />
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">Icon</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead className="w-20">Order</TableHead>
                <TableHead className="w-20">Status</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((category) => {
                const IconComponent = getIconComponent(category.icon);
                return (
                  <TableRow 
                    key={category.id}
                    className="cursor-pointer"
                    onClick={() => openEditEditor(category)}
                  >
                    <TableCell>
                      <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center">
                        <IconComponent className="h-4 w-4 text-primary" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{category.name}</div>
                      {category.description && (
                        <div className="text-xs text-muted-foreground line-clamp-1">
                          {category.description}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        /{category.slug}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-muted-foreground">
                        {category.display_order ?? 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={category.is_active ? 'default' : 'secondary'}>
                        {category.is_active ? 'Active' : 'Draft'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div 
                        className="flex items-center justify-end gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditEditor(category)}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => confirmDelete(category)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Editor Sheet */}
      <AdminSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        title={editingCategory ? 'Edit Category' : 'Add Category'}
        description={editingCategory ? 'Update the category details.' : 'Create a new category for documentation.'}
        onSave={handleSave}
        isSaving={isSaving}
        saveLabel={editingCategory ? 'Update' : 'Create'}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Getting Started"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              value={formData.slug}
              placeholder="auto-generated if empty"
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this category..."
              rows={3}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="icon">Icon</Label>
            <Select
              value={formData.icon}
              onValueChange={(value) => setFormData({ ...formData, icon: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an icon" />
              </SelectTrigger>
              <SelectContent>
                {ICON_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex items-center gap-2">
                      <opt.icon className="h-4 w-4" />
                      {opt.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="display_order">Display Order</Label>
            <Input
              id="display_order"
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="is_active">Active</Label>
              <p className="text-xs text-muted-foreground">Show this category on the public site</p>
            </div>
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
          </div>
        </div>
      </AdminSheet>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        itemName={categoryToDelete?.name}
        itemType="category"
        isDeleting={isDeleting}
      />
    </div>
  );
}